/**
 * Менеджер продуктовой сессии аналитики (session_id).
 *
 * Контракт (см. доку «Virtuoz Analytic Events»):
 *  - один UUID v4 на сессию;
 *  - живёт в sessionStorage (в пределах вкладки) + рядом last_activity_ts;
 *  - та же сессия, пока между действиями < 30 мин; на каждое действие/запрос
 *    обновляем last_activity_ts;
 *  - новый UUID, если сессии нет / прошло ≥ 30 мин неактивности / явный сброс
 *    при login/logout (гость → аккаунт), чтобы таймлайны не сливались;
 *  - session_id уходит и в теле событий, и заголовком X-Session-Id на ВСЕ
 *    запросы (см. shared/api/base.ts).
 */
const SESSION_ID_KEY = "tp_analytics_session_id";
const LAST_ACTIVITY_KEY = "tp_analytics_session_last_activity";
/**
 * Якорный timestamp старта сессии (ISO-8601 UTC). Тот же момент, что у первого
 * web_open. Уходит как `client_ts` в /auth/guest и /auth/verify-code, чтобы
 * бэк проштамповал им якорные события (guest_started/login) — иначе серверный
 * now() кладёт их ПОЗЖЕ клиентских page_view и таймлайн ломается.
 */
const SESSION_START_KEY = "tp_analytics_session_start";
/** Idle timeout: типовое определение веб-сессии — 30 минут неактивности. */
const SESSION_IDLE_MS = 30 * 60 * 1000;
// In-memory fallback, если sessionStorage недоступен (приватный режим и т.п.).
let memorySessionId = null;
let memoryLastActivity = 0;
let memorySessionStart = null;
/** UUID v4 (нативный crypto.randomUUID, иначе crypto.getRandomValues, иначе Math.random). */
export function generateUuid() {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
        if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
            const b = new Uint8Array(16);
            crypto.getRandomValues(b);
            b[6] = (b[6] & 0x0f) | 0x40;
            b[8] = (b[8] & 0x3f) | 0x80;
            const h = Array.from(b, (x) => x.toString(16).padStart(2, "0"));
            return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
        }
    }
    catch {
        /* ignore — упадём в Math.random ниже */
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
function readStore() {
    try {
        const id = sessionStorage.getItem(SESSION_ID_KEY);
        const last = Number(sessionStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
        return { id, last };
    }
    catch {
        return { id: memorySessionId, last: memoryLastActivity };
    }
}
function writeStore(id, last) {
    memorySessionId = id;
    memoryLastActivity = last;
    try {
        sessionStorage.setItem(SESSION_ID_KEY, id);
        sessionStorage.setItem(LAST_ACTIVITY_KEY, String(last));
    }
    catch {
        /* приватный режим — остаёмся на in-memory */
    }
}
function readStart() {
    try {
        return sessionStorage.getItem(SESSION_START_KEY) ?? memorySessionStart;
    }
    catch {
        return memorySessionStart;
    }
}
/** Фиксирует момент старта сессии (при её создании), не трогая на touch. */
function writeStart(ts) {
    memorySessionStart = ts;
    try {
        sessionStorage.setItem(SESSION_START_KEY, ts);
    }
    catch {
        /* приватный режим — остаёмся на in-memory */
    }
}
/**
 * Текущий session_id: переиспользует активную сессию (моложе 30 мин) и продлевает
 * её (touch), либо создаёт новую при отсутствии/протухании. Вызывается на каждый
 * запрос и каждое событие.
 */
export function getSessionId() {
    const now = Date.now();
    const { id, last } = readStore();
    if (!id || now - last >= SESSION_IDLE_MS) {
        const fresh = generateUuid();
        writeStore(fresh, now);
        writeStart(new Date(now).toISOString());
        return fresh;
    }
    writeStore(id, now);
    return id;
}
/**
 * Якорный timestamp старта текущей сессии (ISO-8601 UTC) — момент, к которому
 * привязываются серверные якорные события. Гарантирует, что сессия (и её старт)
 * созданы, поэтому значение ≤ времени первого web_open/page_view.
 */
export function getSessionStartTs() {
    getSessionId();
    return readStart() ?? new Date().toISOString();
}
/** Продлевает текущую сессию (обновляет last_activity_ts), не создавая новую без нужды. */
export function touchSession() {
    getSessionId();
}
/**
 * Принудительно начинает новую сессию (login/logout: гость → аккаунт и обратно),
 * чтобы гостевая и авторизованная части не слились в один таймлайн.
 */
export function resetSession() {
    const now = Date.now();
    const fresh = generateUuid();
    writeStore(fresh, now);
    writeStart(new Date(now).toISOString());
    return fresh;
}
